import { MailOutlined, OrderedListOutlined } from '@ant-design/icons'
import {
  ReturnStepReadable,
  sequencesLeadsListQueryKey,
  sequencesListQueryKey,
  sequencesStepsListQueryKey,
  sequencesStepsPartialUpdateMutation,
  StepTypeEnum
} from '@crm/api'
import { stats, textType } from '@crm/features/campaigns/utils'
import { TableActions } from '@shared/components'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import { App, Card, Flex, Space, Switch, Typography } from 'antd'
import { BaseType } from 'antd/es/typography/Base'

import { Edit2, Trash } from 'iconsax-react'

import { useParams } from 'react-router-dom'

import type { MenuProps } from 'antd'

const { Text, Title } = Typography

type Props = {
  dragHandler: React.ReactNode
  step: ReturnStepReadable
  onEdit: React.Dispatch<React.SetStateAction<number | undefined>>
  onDelete: React.Dispatch<React.SetStateAction<number | undefined>>
}

export const StepsCard = ({ step, dragHandler, onEdit, onDelete }: Props) => {
  const { t } = useLocales()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { notification } = App.useApp()

  const { id } = useParams()

  const { mutate } = useMutation({
    ...sequencesStepsPartialUpdateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([
        sequencesLeadsListQueryKey({
          path: { sequence_id: String(id) }
        }),
        sequencesListQueryKey(),
        sequencesStepsListQueryKey({
          path: { sequence_id: String(id) }
        })
      ])

      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  const onSubmit = (enabled: boolean) =>
    mutate({
      path: { sequence_id: String(id), id: step.id },
      body: { enabled }
    })

  const getActionColumnItem: () => MenuProps['items'] = () => [
    {
      label: t('buttons.edit'),
      key: 'edit',
      icon: <Edit2 size={20} />,
      onClick: () => onEdit(step.id)
    },
    { type: 'divider' },
    {
      label: t('buttons.delete'),
      key: 'delete',
      icon: <Trash size={20} />,
      danger: true,
      onClick: () => onDelete(step.id)
    }
  ]

  return (
    <Card
      title={
        <Flex gap={16} align="center">
          {step.number}
          <Title className="mb-0" level={5}>
            <Flex align="center" gap={8}>
              {step.step_type ? (
                <div>
                  {step.step_type === StepTypeEnum.TASK ? (
                    <OrderedListOutlined />
                  ) : (
                    <MailOutlined />
                  )}
                </div>
              ) : null}
              {step.title} - {step.wait_type}: {step.wait_time}
            </Flex>
          </Title>
        </Flex>
      }
    >
      <Flex align="center" gap={16}>
        {dragHandler}

        <Flex
          justify="space-between"
          align="center"
          className="w-full"
          gap={16}
        >
          <Flex align="center" gap={10}>
            <Switch checked={step.enabled} onChange={onSubmit} />
            {step.description}
          </Flex>

          <Flex gap={16} align="center">
            {stats?.map((i) => (
              <Text
                style={{ whiteSpace: 'nowrap' }}
                key={`campaigns--${i}`}
                type={textType[i as keyof typeof textType] as BaseType}
              >
                <Space align="center" direction="vertical" size={0}>
                  {t(`campaigns.${i}`)}
                  {step[i as keyof typeof step]}
                </Space>
              </Text>
            ))}

            <TableActions options={getActionColumnItem()} />
          </Flex>
        </Flex>
      </Flex>
    </Card>
  )
}
